import React from 'react';
import { Inbox } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button, type ButtonProps } from './Button';

export interface EmptyStateAction extends Omit<ButtonProps, 'children'> {
  label: string;
}

export interface EmptyStateProps {
  /** Icon to display — defaults to Inbox */
  icon?: React.ReactNode;
  title: string;
  description?: string;
  /** Primary action button */
  action?: EmptyStateAction;
  /** Secondary action button */
  secondaryAction?: EmptyStateAction;
  className?: string;
}

export function EmptyState({
  icon,
  title,
  description,
  action,
  secondaryAction,
  className,
}: EmptyStateProps) {
  const { label: actionLabel, ...actionProps } = action ?? { label: '' };
  const { label: secondaryLabel, ...secondaryProps } = secondaryAction ?? { label: '' };

  return (
    <div className={cn('flex flex-col items-center justify-center py-16 px-4 text-center', className)}>
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-slate-100 mb-4">
        {icon ?? <Inbox className="h-8 w-8 text-slate-400" aria-hidden="true" />}
      </div>

      <h3 className="text-sm font-semibold text-slate-900 mb-1">{title}</h3>

      {description && (
        <p className="text-sm text-slate-500 max-w-sm">{description}</p>
      )}

      {(action || secondaryAction) && (
        <div className="mt-6 flex items-center gap-3">
          {action && (
            <Button variant="primary" size="sm" {...actionProps}>
              {actionLabel}
            </Button>
          )}
          {secondaryAction && (
            <Button variant="secondary" size="sm" {...secondaryProps}>
              {secondaryLabel}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
